<template>
  <div>
    <v-card-text class="py-6 px-4">
      <!-- Leaderboard preview (mock data) -->
      <div class="mb-6" data-testid="leaderboard-preview">
        <v-card elevation="1" class="top-sales-leaders rounded-lg">
          <div class="leaderboard-header d-flex justify-space-between align-center pa-3">
            <h4
              :style="{
                color: titleColor,
                fontWeight: titleBold ? 'bold' : 'normal',
                fontStyle: titleItalic ? 'italic' : 'normal',
                textDecoration: titleUnderline ? 'underline' : 'none',
                textAlign: horizontalAlignment,
                fontSize: titleFontSize,
              }"
            >
              {{ title }}
            </h4>
          </div>
          <section class="leaderboard pa-2">
            <div
              v-for="(participant, index) in mockParticipants"
              :key="index"
              class="leaderboard-row d-flex align-center pa-2 mb-1"
              :class="{ 'active-row': participant.isCurrentUser }"
              :style="
                participant.isCurrentUser
                  ? { background: highlightColor, borderRadius: '4px' }
                  : { borderRadius: '4px' }
              "
            >
              <div class="rank mr-3 font-weight-bold" style="min-width: 24px; text-align: center">
                {{ participant.rank }}
              </div>
              <div
                class="avatar mr-2 d-flex align-center justify-center"
                style="width: 32px; height: 32px; border-radius: 50%; background: #e0e0e0"
              >
                <span class="caption">{{ participant.initials }}</span>
              </div>
              <span class="name flex-grow-1">{{
                participant.isCurrentUser ? "You" : participant.displayName
              }}</span>
              <span class="amount font-weight-bold">{{ participant.progressPercent }}%</span>
            </div>
          </section>
        </v-card>
      </div>
      <!-- Title section -->
      <p class="subtitle-1 font-weight-bold mb-2">Title</p>
      <v-text-field
        v-model="title"
        label="Title"
        outlined
        dense
        hide-details
        data-testid="title-input"
        class="mb-4"
      />

      <!-- Title styling row -->
      <div class="d-flex align-center mb-4">
        <input
          type="color"
          v-model="titleColor"
          aria-label="Title color"
          data-testid="title-color-picker"
          class="color-swatch mr-3"
        />
        <v-btn
          icon
          small
          :color="titleBold ? 'brandCyan' : ''"
          @click="titleBold = !titleBold"
          aria-label="Bold"
          data-testid="bold-btn"
        >
          <v-icon>mdi-format-bold</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :color="titleItalic ? 'brandCyan' : ''"
          @click="titleItalic = !titleItalic"
          aria-label="Italic"
          data-testid="italic-btn"
        >
          <v-icon>mdi-format-italic</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :color="titleUnderline ? 'brandCyan' : ''"
          @click="titleUnderline = !titleUnderline"
          aria-label="Underline"
          data-testid="underline-btn"
        >
          <v-icon>mdi-format-underline</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :color="horizontalAlignment === 'left' ? 'brandCyan' : ''"
          @click="horizontalAlignment = 'left'"
          aria-label="Align left"
          data-testid="align-left-btn"
        >
          <v-icon>mdi-format-align-left</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :color="horizontalAlignment === 'center' ? 'brandCyan' : ''"
          @click="horizontalAlignment = 'center'"
          aria-label="Align center"
          data-testid="align-center-btn"
        >
          <v-icon>mdi-format-align-center</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :color="horizontalAlignment === 'right' ? 'brandCyan' : ''"
          @click="horizontalAlignment = 'right'"
          aria-label="Align right"
          data-testid="align-right-btn"
        >
          <v-icon>mdi-format-align-right</v-icon>
        </v-btn>
        <v-select
          v-model="titleSize"
          :items="titleSizeItems"
          label="Size"
          outlined
          dense
          hide-details
          data-testid="title-size-select"
          class="ml-3"
          style="max-width: 120px"
        />
      </div>

      <!-- Highlight color section -->
      <p class="subtitle-1 font-weight-bold mb-2">Highlight Color</p>
      <div class="d-flex align-center mb-6">
        <input
          type="color"
          v-model="highlightColor"
          aria-label="Highlight color"
          data-testid="highlight-color-swatch"
          class="color-swatch mr-3"
        />
        <v-text-field
          v-model="highlightColor"
          label="Color (in hexadecimal)"
          outlined
          dense
          hide-details
          data-testid="highlight-color-hex"
        />
      </div>
    </v-card-text>

    <!-- Footer -->
    <v-alert v-if="saveError" type="error" dense text class="mx-4 mb-2" data-testid="save-error">{{
      saveError
    }}</v-alert>

    <div class="d-flex align-center px-4 pb-4">
      <Button
        data-testid="switch-to-content-btn"
        text
        color="brandCyan"
        @click="handleSwitchToContent"
        >Switch to Content/Image</Button
      >
      <v-spacer />
      <Button
        :dataTestId="'cancel-btn'"
        text
        color="brandPrimary"
        class="mr-4"
        @click="handleCancel"
        >Cancel</Button
      >
      <Button
        :dataTestId="'save-btn'"
        color="brandPrimary"
        :loading="saving"
        :disabled="saving"
        @click="onSave"
        >Save</Button
      >
    </div>

    <!-- Unsaved changes modal -->
    <UnsavedChangesDialog v-model="showUnsavedModal" @stay="handleStay" @discard="handleDiscard" />
  </div>
</template>

<script>
import Button from "@/components/generic/Button.vue";
import UnsavedChangesDialog from "@/components/generic/UnsavedChangesDialog.vue";
import ProgramService from "@/services/ProgramService";
import { mapState } from "vuex";

const MOCK_PARTICIPANTS = [
  {
    rank: 3,
    displayName: "Mia Thompson",
    initials: "MT",
    progressPercent: 82,
    isCurrentUser: false,
  },
  {
    rank: 4,
    displayName: "Jake Reeves",
    initials: "JR",
    progressPercent: 78,
    isCurrentUser: false,
  },
  { rank: 5, displayName: "You", initials: "YO", progressPercent: 71, isCurrentUser: true },
  {
    rank: 6,
    displayName: "Leila Patel",
    initials: "LP",
    progressPercent: 68,
    isCurrentUser: false,
  },
  { rank: 7, displayName: "Sam Ortiz", initials: "SO", progressPercent: 62, isCurrentUser: false },
];

function initialState() {
  return {
    isDirty: false,
    showUnsavedModal: false,
    unsavedModalAction: null,
    saving: false,
    saveError: null,
    title: "Leaderboard",
    titleColor: "#000000",
    titleBold: false,
    titleItalic: false,
    titleUnderline: false,
    titleSize: "md",
    horizontalAlignment: "center",
    highlightColor: "#E4F2F5",
    rowId: null,
  };
}

export default {
  name: "LeaderboardCreator",
  components: { Button, UnsavedChangesDialog },
  props: {
    row: { type: Object, default: () => ({}) },
    column: { type: Object, default: null },
    type: { type: String, default: "leaderboard" },
    widgetTypes: { type: Array, default: () => [] },
    rowCount: { type: Number, default: 0 },
    position: { type: Number, default: 1 },
  },
  data() {
    return { ...initialState() };
  },
  computed: {
    ...mapState(["globalClientId"]),
    mockParticipants() {
      return MOCK_PARTICIPANTS;
    },
    titleFontSize() {
      const sizes = { sm: "14px", md: "16px", lg: "20px", xl: "24px" };
      return sizes[this.titleSize] || "16px";
    },
    titleSizeItems() {
      return [
        { text: "Small", value: "sm" },
        { text: "Medium", value: "md" },
        { text: "Large", value: "lg" },
        { text: "Extra Large", value: "xl" },
      ];
    },
  },
  watch: {
    title() {
      this.markDirty();
    },
    titleColor() {
      this.markDirty();
    },
    titleBold() {
      this.markDirty();
    },
    titleItalic() {
      this.markDirty();
    },
    titleUnderline() {
      this.markDirty();
    },
    titleSize() {
      this.markDirty();
    },
    horizontalAlignment() {
      this.markDirty();
    },
    highlightColor() {
      this.markDirty();
    },
  },
  created() {
    this._preloading = true;
    this._createdColumnId = null;
    this.preloadRow();
    this.$nextTick(() => {
      this._preloading = false;
      this.isDirty = false;
    });
  },
  methods: {
    preloadRow() {
      const column = this.column || this.row?.DashboardColumns?.[0];
      if (!column) return;
      const config = column.config;
      if (!config) return;

      if (config.title) {
        if (config.title.text !== undefined) this.title = config.title.text;
        if (config.title.color !== undefined) this.titleColor = config.title.color;
        if (config.title.bold !== undefined) this.titleBold = config.title.bold;
        if (config.title.italic !== undefined) this.titleItalic = config.title.italic;
        if (config.title.underline !== undefined) this.titleUnderline = config.title.underline;
        if (config.title.size !== undefined) this.titleSize = config.title.size;
        if (config.title.horizontalAlignment !== undefined) {
          this.horizontalAlignment = config.title.horizontalAlignment;
        } else if (config.title.align !== undefined) {
          this.horizontalAlignment = config.title.align;
        }
      }
      if (config.highlightColor !== undefined) this.highlightColor = config.highlightColor;
    },

    buildConfig() {
      return {
        title: {
          text: this.title,
          color: this.titleColor,
          bold: this.titleBold,
          italic: this.titleItalic,
          underline: this.titleUnderline,
          size: this.titleSize,
          horizontalAlignment: this.horizontalAlignment,
        },
        highlightColor: this.highlightColor,
      };
    },

    async onSave() {
      if (this.saving) return;
      if (!this.title.trim()) {
        this.saveError = "Title is required";
        return;
      }
      this.saving = true;
      this.saveError = null;
      try {
        const config = this.buildConfig();
        let columnId = this.row?.DashboardColumns?.[0]?.columnId || this._createdColumnId;
        let isNew = !this.row?.DashboardColumns?.[0]?.columnId && !this._createdColumnId;

        if (isNew) {
          // 1. Create row if needed
          let rowId = this.row?.rowId || this.rowId;
          if (!rowId) {
            const rowResponse = await ProgramService.createDashboardRow({
              rowTypeId: this.row.rowTypeId,
              programId: this.row.programId,
              position: this.rowCount + 1,
            });
            rowId = rowResponse.data.result.rowId;
            this.rowId = rowId;
          }

          // 2. Create column
          const widgetTypeId = this.widgetTypes?.find((wt) => wt.type === this.type)?.widgetTypeId;
          const colResponse = await ProgramService.createDashboardColumn({
            rowId,
            widgetTypeId,
            config,
            position: this.position,
            clientId: this.globalClientId,
          });
          columnId = colResponse.data.result.columnId;
          this._createdColumnId = columnId;
        } else {
          // Edit — update existing column
          await ProgramService.updateDashboardColumn(columnId, { config });
        }

        const savedRowId = this.row?.rowId || this.rowId;
        this.$emit("saved", { columnId, config, isNew, rowId: savedRowId });
        this.$emit("refresh");
        this.$emit("close");
      } catch (e) {
        this.saveError = e.message || "Save failed";
      } finally {
        this.saving = false;
      }
    },

    handleBack() {
      if (this.isDirty) {
        this.unsavedModalAction = "close";
        this.showUnsavedModal = true;
      } else {
        this.$emit("close");
      }
    },
    handleCancel() {
      if (this.isDirty) {
        this.unsavedModalAction = "close";
        this.showUnsavedModal = true;
      } else {
        this.$emit("close");
      }
    },
    handleSwitchToContent() {
      if (this.isDirty) {
        this.unsavedModalAction = "switch-to-content";
        this.showUnsavedModal = true;
      } else {
        this.$emit("switch-to-content");
      }
    },
    handleDiscard() {
      this.showUnsavedModal = false;
      if (this.unsavedModalAction === "switch-to-content") {
        this.$emit("switch-to-content");
      } else {
        this.$emit("close");
      }
      this.unsavedModalAction = null;
    },
    handleStay() {
      this.showUnsavedModal = false;
      this.unsavedModalAction = null;
    },

    markDirty() {
      if (!this._preloading) this.isDirty = true;
    },
  },
};
</script>

<style scoped>
.color-swatch {
  width: 40px;
  height: 40px;
  min-width: 40px;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0;
  padding: 2px;
  appearance: none;
  -webkit-appearance: none;
}
</style>
